import * as React from 'react';
import { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  IconButton,
  Paper,
  useTheme,
  alpha,
  Skeleton,
  Divider,
  Avatar,
  Stack,
  Chip,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { verifyInviteToken } from '../../store/userThunk';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  fetchARDashboardNumbers,
  fetchARDashboardSales,
  fetchARDashboardAging,
  fetchARDashboardCustomersDue
} from '../../store/ARDashboardStore/ARDashboardThunk';
import {
  fetchAPDashboardNumbers,
  fetchAPDashboardSales,
  fetchAPDashboardCustomersDue
} from '../../store/APDashboardStore/APDashboardThunk';
import { ApiStatus } from '../../utils/api.types';
import { Utils } from '../../utils/common-utils';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PeopleIcon from '@mui/icons-material/People';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PaymentIcon from '@mui/icons-material/Payment';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import RefreshIcon from '@mui/icons-material/Refresh';
import InsightsIcon from '@mui/icons-material/Insights';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CloseIcon from '@mui/icons-material/Close';
import Chart from 'react-apexcharts';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import Popover from '@mui/material/Popover';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';

const ACCENT = '#84cc16';
const ACCENT_LIGHT = '#d9f99d';
const ACCENT_SOFT = '#ecfccb';
const BG = '#f5f7fa';
const SURFACE = '#ffffff';
const TEXT = '#0f172a';
const MUTED = '#64748b';
const BORDER = '#eef0f3';

const cardSx = {
  borderRadius: 4,
  border: `1px solid ${BORDER}`,
  boxShadow: 'none',
  backgroundColor: SURFACE
};

const Home = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { user, selectedAccount } = useAppSelector(state => state.auth);
  const [searchParams] = useSearchParams();
  const [period, setPeriod] = useState('30days');
  const [isLoading, setIsLoading] = useState(false);
  const [calAnchor, setCalAnchor] = useState<HTMLElement | null>(null);
  const [customStart, setCustomStart] = useState<Date | null>(null);
  const [customEnd, setCustomEnd] = useState<Date | null>(null);
  const [appliedRange, setAppliedRange] = useState<{ start: Date; end: Date } | null>(null);

  const ARDashboard = useAppSelector(state => state.ARDashboard);
  const APDashboard = useAppSelector(state => state.APDashboard);

  useEffect(() => {
    const token = searchParams.get('inviteToken');
    if (token) {
      dispatch(verifyInviteToken({ token }));
    }
  }, [dispatch, searchParams]);

  useEffect(() => {
    if (selectedAccount?._id) {
      fetchDashboardData();
    }
  }, [selectedAccount, period, appliedRange]);

  const fetchDashboardData = async () => {
    if (!selectedAccount?._id) {
      console.warn('No selected account found');
      return;
    }

    setIsLoading(true);

    const fmt = (d: Date) => d.toISOString().slice(0, 10);
    let start: Date;
    let end: Date;
    if (appliedRange) {
      start = appliedRange.start;
      end = appliedRange.end;
    } else {
      const daysMap: Record<string, number> = {
        '7days': 7,
        '30days': 30,
        '90days': 90,
        '365days': 365
      };
      const days = daysMap[period] || 30;
      end = new Date();
      start = new Date();
      start.setDate(end.getDate() - days + 1);
    }
    const filters = `start=${fmt(start)}&end=${fmt(end)}&period=${period}`;

    try {
      const arPromises = [
        dispatch(fetchARDashboardNumbers({ accountId: selectedAccount._id, params: filters })),
        dispatch(fetchARDashboardSales({ accountId: selectedAccount._id, params: filters })),
        dispatch(fetchARDashboardAging({ accountId: selectedAccount._id, params: filters })),
        dispatch(fetchARDashboardCustomersDue({ accountId: selectedAccount._id, params: filters }))
      ];

      const apPromises = [
        dispatch(fetchAPDashboardNumbers({ accountId: selectedAccount._id, params: filters })),
        dispatch(fetchAPDashboardSales({ accountId: selectedAccount._id, params: filters })),
        dispatch(fetchAPDashboardCustomersDue({ accountId: selectedAccount._id, params: filters }))
      ];

      await Promise.allSettled([...arPromises, ...apPromises]);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const MetricCard = ({
    title,
    value,
    trend,
    icon,
    accent = ACCENT,
    highlight = false
  }: {
    title: string;
    value: string | number;
    trend?: { hasGrown: boolean; value: number };
    icon: React.ReactNode;
    accent?: string;
    highlight?: boolean;
  }) => (
    <Card
      sx={{
        ...cardSx,
        backgroundColor: highlight ? ACCENT_SOFT : SURFACE,
        borderColor: highlight ? ACCENT_LIGHT : BORDER,
        height: '100%',
        minHeight: 110,
        transition: 'transform .2s ease, box-shadow .2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 24px -12px rgba(15,23,42,0.12)'
        }
      }}
    >
      <CardContent sx={{
        p: 1.75,
        '&:last-child': { pb: 1.75 },
        overflow: 'hidden',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between'
      }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
          <Box flex={1} minWidth={0}>
            <Typography
              sx={{ color: MUTED, fontWeight: 600, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.4px', lineHeight: 1.3, mb: 0.75 }}
              noWrap
            >
              {title}
            </Typography>
            <Typography
              sx={{ fontWeight: 700, color: TEXT, lineHeight: 1.2, fontSize: '1rem' }}
              noWrap
            >
              {isLoading ? <Skeleton width={70} /> : value}
            </Typography>
          </Box>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: highlight ? '#ffffff' : alpha(accent, 0.12),
              color: accent,
              flexShrink: 0
            }}
          >
            {React.cloneElement(icon as React.ReactElement, { sx: { fontSize: 17, color: accent } })}
          </Box>
        </Stack>
        <Box sx={{ height: 20, mt: 1, display: 'flex', alignItems: 'center' }}>
          {trend && trend.value !== null && trend.value !== undefined ? (
            <Chip
              size="small"
              icon={trend.hasGrown
                ? <TrendingUpIcon sx={{ fontSize: 11, color: '#16a34a !important' }} />
                : <TrendingDownIcon sx={{ fontSize: 11, color: '#dc2626 !important' }} />}
              label={`${Number(trend.value).toFixed(1)}% vs prev`}
              sx={{
                height: 18,
                fontSize: '0.62rem',
                fontWeight: 600,
                color: trend.hasGrown ? '#16a34a' : '#dc2626',
                backgroundColor: trend.hasGrown ? '#dcfce7' : '#fee2e2',
                '& .MuiChip-icon': { ml: '3px', mr: '-2px' },
                '& .MuiChip-label': { px: 0.75 }
              }}
            />
          ) : (
            <Typography sx={{ color: MUTED, fontSize: '0.62rem', fontStyle: 'italic' }}>
              —
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );

  // Aggregate daily series into buckets for readability
  const aggregateSeries = (series: Record<string, any> | null | undefined, bucketCount: number = 12) => {
    if (!series) return { labels: [], values: [] };
    const entries = Object.entries(series).map(([k, v]) => ({ date: new Date(k), value: Number(v) || 0 }));
    entries.sort((a, b) => a.date.getTime() - b.date.getTime());
    if (entries.length === 0) return { labels: [], values: [] };
    if (entries.length <= bucketCount) {
      return {
        labels: entries.map(e => e.date.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })),
        values: entries.map(e => e.value)
      };
    }
    const bucketSize = Math.ceil(entries.length / bucketCount);
    const labels: string[] = [];
    const values: number[] = [];
    for (let i = 0; i < entries.length; i += bucketSize) {
      const chunk = entries.slice(i, i + bucketSize);
      const sum = chunk.reduce((s, e) => s + e.value, 0);
      const start = chunk[0].date;
      const end = chunk[chunk.length - 1].date;
      const sameMonth = start.getMonth() === end.getMonth();
      labels.push(sameMonth
        ? start.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })
        : start.toLocaleDateString('en-US', { month: 'short' }));
      values.push(sum);
    }
    return { labels, values };
  };

  const arAgg = aggregateSeries(ARDashboard.sales?.dashboardSales?.series, 12);
  const apAgg = aggregateSeries(APDashboard.sales?.dashboardSales?.series, 12);
  const trendCategories = arAgg.labels.length >= apAgg.labels.length ? arAgg.labels : apAgg.labels;

  const salesChartOptions = {
    chart: {
      type: 'line' as const,
      height: 340,
      toolbar: { show: false },
      fontFamily: 'inherit',
      stacked: false,
      animations: { enabled: true, easing: 'easeinout' as const, speed: 600 }
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '55%',
        borderRadius: 6,
        borderRadiusApplication: 'end' as const
      }
    },
    dataLabels: { enabled: false },
    stroke: { show: true, width: [0, 3], curve: 'smooth' as const },
    xaxis: {
      categories: trendCategories,
      labels: { style: { colors: MUTED, fontSize: '11px' } },
      axisBorder: { show: false },
      axisTicks: { show: false }
    },
    yaxis: {
      labels: {
        style: { colors: MUTED, fontSize: '11px' },
        formatter: (value: number) => Utils.formatNumber(value, true, false)
      }
    },
    colors: [ACCENT, '#f59e0b'],
    fill: {
      opacity: [0.95, 1],
      type: ['solid', 'solid']
    },
    markers: {
      size: [0, 5],
      colors: ['#f59e0b'],
      strokeColors: '#fff',
      strokeWidth: 2,
      hover: { size: 7 }
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: { formatter: (value: number) => Utils.formatNumber(value, true, false) }
    },
    grid: {
      borderColor: BORDER,
      strokeDashArray: 4,
      padding: { left: 8, right: 8 },
      yaxis: { lines: { show: true } },
      xaxis: { lines: { show: false } }
    },
    legend: {
      position: 'top' as const,
      horizontalAlign: 'right' as const,
      fontSize: '12px',
      markers: { width: 10, height: 10, radius: 12 } as any,
      labels: { colors: MUTED },
      itemMargin: { horizontal: 12, vertical: 0 }
    }
  };

  const salesChartSeries = [
    { name: 'Sales Revenue', type: 'column', data: arAgg.values },
    { name: 'Purchase Amount', type: 'line', data: apAgg.values }
  ];

  // Aging Chart - simple pie with side total
  const agingChartOptions = {
    chart: { type: 'donut' as const, height: 220, fontFamily: 'inherit' },
    labels: ARDashboard.aging?.dashboardAging?.series ?
      Object.keys(ARDashboard.aging.dashboardAging.series) : [],
    colors: [ACCENT, '#f59e0b', '#ef4444', '#8b5cf6', '#3b82f6'],
    dataLabels: {
      enabled: true,
      style: { fontSize: '11px', fontWeight: 700, colors: ['#fff'] },
      formatter: (val: number) => `${val.toFixed(0)}%`,
      dropShadow: { enabled: false }
    },
    stroke: { width: 2, colors: ['#fff'] },
    legend: {
      position: 'bottom' as const,
      horizontalAlign: 'center' as const,
      fontSize: '11px',
      markers: { width: 8, height: 8, radius: 12 } as any,
      labels: { colors: MUTED },
      itemMargin: { horizontal: 6, vertical: 2 }
    },
    plotOptions: {
      pie: {
        donut: { size: '60%', labels: { show: false } }
      }
    },
    tooltip: {
      y: { formatter: (val: number) => Utils.formatNumber(val, true, false) }
    }
  };

  const agingChartSeries = ARDashboard.aging?.dashboardAging?.series ?
    Object.values(ARDashboard.aging.dashboardAging.series).map(val => Number(val) || 0) : [];

  // Sales vs Received - use numbers totalAR + toCollect (balance) for accurate totals
  const arNums: any = ARDashboard.numbers?.dashboardNumbers || {};
  const totalSales = Number(arNums.totalAR || ARDashboard.sales?.dashboardSales?.salesValue || 0);
  const outstandingAR = Number(arNums.toCollect || 0);
  const arFullyPaidAmt = Number(arNums.arFullyPaid || 0);
  const arPartiallyPaidReceivedAmt = Number(arNums.arPartiallyPaidReceived || 0);
  const amountReceived = arFullyPaidAmt + arPartiallyPaidReceivedAmt;

  const barBase = {
    chart: { type: 'bar' as const, height: 340, toolbar: { show: false }, fontFamily: 'inherit' },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '45%',
        borderRadius: 8,
        borderRadiusApplication: 'end' as const,
        distributed: true,
        dataLabels: { position: 'top' as const }
      }
    },
    dataLabels: {
      enabled: true,
      formatter: (val: number) => Utils.formatNumber(val, true, false),
      offsetY: -24,
      style: { fontSize: '11px', fontWeight: 600, colors: [TEXT] }
    },
    yaxis: {
      labels: {
        style: { colors: MUTED, fontSize: '11px' },
        formatter: (value: number) => Utils.formatNumber(value, true, false)
      },
      forceNiceScale: true,
      max: (max: number) => max * 1.25
    },
    legend: { show: false },
    tooltip: { y: { formatter: (value: number) => Utils.formatNumber(value, true, false) } },
    grid: { borderColor: BORDER, strokeDashArray: 4, xaxis: { lines: { show: false } }, padding: { top: 24 } }
  };

  const salesReceivedChartOptions = {
    ...barBase,
    xaxis: {
      categories: ['Total Sales', 'Received', 'Outstanding'],
      labels: { style: { colors: MUTED, fontSize: '11px' } },
      axisBorder: { show: false },
      axisTicks: { show: false }
    },
    colors: [ACCENT, '#22c55e', '#ef4444'],
    tooltip: {
      y: { formatter: (value: number) => Utils.formatNumber(value, true, false) },
      custom: ({ dataPointIndex }: any) => {
        if (dataPointIndex === 1) {
          return `<div style="padding:8px 12px;font-family:inherit;background:#fff;min-width:200px;">
            <b style="color:#0f172a;font-size:13px;">Received</b><br/>
            <span style="color:#22c55e;font-weight:700;font-size:14px;">${Utils.formatNumber(amountReceived, true, false)}</span>
            <div style="margin-top:6px;font-size:11px;color:#64748b;">
              Fully Paid: <b style="color:#22c55e;">${Utils.formatNumber(arFullyPaidAmt, true, false)}</b><br/>
              Partially Paid (Received): <b style="color:#3b82f6;">${Utils.formatNumber(arPartiallyPaidReceivedAmt, true, false)}</b>
            </div>
          </div>`;
        }
        const labels = ['Total Sales', 'Received', 'Outstanding'];
        const values = [totalSales, amountReceived, outstandingAR];
        const colors = ['#84cc16', '#22c55e', '#ef4444'];
        return `<div style="padding:8px 12px;font-family:inherit;background:#fff;"><b style="color:#0f172a;font-size:13px;">${labels[dataPointIndex]}</b><br/><span style="color:${colors[dataPointIndex]};font-weight:700;font-size:14px;">${Utils.formatNumber(values[dataPointIndex], true, false)}</span></div>`;
      }
    }
  };

  const salesReceivedChartSeries = [{
    name: 'Amount',
    data: [totalSales, amountReceived, outstandingAR]
  }];

  const apNums: any = APDashboard.numbers?.dashboardNumbers || {};
  const totalPurchase = Number(apNums.totalAP || APDashboard.sales?.dashboardSales?.salesValue || 0);
  const outstandingAP = Number(apNums.toPay || 0);
  const apFullyPaidAmt = Number(apNums.apFullyPaid || 0);
  const apPartiallyPaidReceivedAmt = Number(apNums.apPartiallyPaidReceived || 0);
  const amountPaid = apFullyPaidAmt + apPartiallyPaidReceivedAmt;
  console.log('AP nums:', apNums, 'amountPaid:', amountPaid);

  const purchasePaidChartOptions = {
    ...barBase,
    xaxis: {
      categories: ['Total Purchase', 'Paid', 'Outstanding'],
      labels: { style: { colors: MUTED, fontSize: '11px' } },
      axisBorder: { show: false },
      axisTicks: { show: false }
    },
    colors: ['#f59e0b', '#22c55e', '#ef4444'],
    tooltip: {
      y: { formatter: (value: number) => Utils.formatNumber(value, true, false) },
      custom: ({ dataPointIndex }: any) => {
        if (dataPointIndex === 1) {
          return `<div style="padding:8px 12px;font-family:inherit;background:#fff;min-width:200px;">
            <b style="color:#0f172a;font-size:13px;">Paid</b><br/>
            <span style="color:#22c55e;font-weight:700;font-size:14px;">${Utils.formatNumber(amountPaid, true, false)}</span>
            <div style="margin-top:6px;font-size:11px;color:#64748b;">
              Fully Paid: <b style="color:#22c55e;">${Utils.formatNumber(apFullyPaidAmt, true, false)}</b><br/>
              Partially Paid (Paid): <b style="color:#3b82f6;">${Utils.formatNumber(apPartiallyPaidReceivedAmt, true, false)}</b>
            </div>
          </div>`;
        }
        const labels = ['Total Purchase', 'Paid', 'Outstanding'];
        const values = [totalPurchase, amountPaid, outstandingAP];
        const colors = ['#f59e0b', '#22c55e', '#ef4444'];
        return `<div style="padding:8px 12px;font-family:inherit;background:#fff;"><b style="color:#0f172a;font-size:13px;">${labels[dataPointIndex]}</b><br/><span style="color:${colors[dataPointIndex]};font-weight:700;font-size:14px;">${Utils.formatNumber(values[dataPointIndex], true, false)}</span></div>`;
      }
    }
  };

  const purchasePaidChartSeries = [{
    name: 'Amount',
    data: [totalPurchase, amountPaid, outstandingAP]
  }];

  const handlePeriodChange = (_e: any, newPeriod: string | null) => {
    if (newPeriod) {
      setPeriod(newPeriod);
      setAppliedRange(null);
    }
  };

  const applyCustomRange = () => {
    if (customStart && customEnd) {
      setAppliedRange({ start: customStart, end: customEnd });
      setCalAnchor(null);
    }
  };

  const clearCustomRange = () => {
    setAppliedRange(null);
    setCustomStart(null);
    setCustomEnd(null);
  };

  const rangeLabel = appliedRange
    ? `${appliedRange.start.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })} – ${appliedRange.end.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: '2-digit' })}`
    : null;

  const initials = (name?: string) =>
    (name || 'NA').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();

  const avatarColors = ['#84cc16', '#f59e0b', '#3b82f6', '#8b5cf6', '#ef4444'];

  const PerformerRow = ({ item, index, color }: { item: any; index: number; color: string }) => (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.25}
      sx={{
        py: 0.65,
        px: 0.5,
        borderRadius: 2,
        transition: 'background-color .2s',
        '&:hover': { backgroundColor: alpha(color, 0.06) }
      }}
    >
      <Avatar
        sx={{
          width: 30, height: 30,
          backgroundColor: alpha(color, 0.15),
          color,
          fontWeight: 700,
          fontSize: '0.7rem'
        }}
      >
        {initials(item.customer?.name)}
      </Avatar>
      <Box flex={1} minWidth={0}>
        <Typography sx={{ fontWeight: 600, color: TEXT, fontSize: '0.75rem' }} noWrap>
          {item.customer?.name || 'N/A'}
        </Typography>
        <Typography sx={{ color: MUTED, fontSize: '0.65rem' }}>
          {Utils.formatNumber(item.totalAmount || 0, true, false)}
        </Typography>
      </Box>
      <Box textAlign="right">
        <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: '0.75rem' }}>
          {Utils.formatNumber(item.totalOutstanding || 0, true, false)}
        </Typography>
        <Typography sx={{ color: MUTED, fontSize: '0.6rem' }}>
          Outstanding
        </Typography>
      </Box>
    </Stack>
  );

  return (
    <Box sx={{ p: { xs: 1.25, sm: 1.5, md: 2 }, backgroundColor: BG, minHeight: '100vh', overflowX: 'hidden' }}>
      {/* Header Row */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        spacing={1.5}
        sx={{ mb: 1.5 }}
      >
        <Box>
          <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
            Hi {user?.name || 'there'} 👋
          </Typography>
          <Typography sx={{ color: MUTED, fontSize: '0.75rem' }}>
            {new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })} · {selectedAccount?.name || 'your account'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: { xs: '100%', md: 'auto' }, flexWrap: 'wrap' }}>
          <Box sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            height: 38,
            px: 1.5,
            borderRadius: 999,
            backgroundColor: SURFACE,
            border: `1px solid ${BORDER}`,
            boxSizing: 'border-box'
          }}>
            <PeopleIcon sx={{ fontSize: 14, color: '#8b5cf6' }} />
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: MUTED, lineHeight: 1 }}>Customers</Typography>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: TEXT, lineHeight: 1 }}>
              {ARDashboard.numbers?.dashboardNumbers?.totalCustomers || 0}
            </Typography>
          </Box>
          <Box sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            height: 38,
            px: 1.5,
            borderRadius: 999,
            backgroundColor: SURFACE,
            border: `1px solid ${BORDER}`,
            boxSizing: 'border-box'
          }}>
            <LocalShippingIcon sx={{ fontSize: 14, color: '#ec4899' }} />
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: MUTED, lineHeight: 1 }}>Vendors</Typography>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: TEXT, lineHeight: 1 }}>
              {ARDashboard.numbers?.dashboardNumbers?.totalVendors || 0}
            </Typography>
          </Box>
          <ToggleButtonGroup
            value={period}
            exclusive
            onChange={handlePeriodChange}
            size="small"
            sx={{
              backgroundColor: SURFACE,
              border: `1px solid ${BORDER}`,
              borderRadius: 999,
              p: 0.5,
              flex: { xs: 1, md: 'unset' },
              '& .MuiToggleButton-root': {
                border: 0,
                px: { xs: 1.25, sm: 2 },
                py: 0.5,
                fontSize: '0.75rem',
                fontWeight: 600,
                textTransform: 'none',
                color: MUTED,
                flex: { xs: 1, md: 'unset' },
                borderRadius: '999px !important',
                '&.Mui-selected': {
                  backgroundColor: TEXT,
                  color: '#fff',
                  '&:hover': { backgroundColor: TEXT }
                }
              }
            }}
          >
            <ToggleButton value="7days">7D</ToggleButton>
            <ToggleButton value="30days">30D</ToggleButton>
            <ToggleButton value="90days">90D</ToggleButton>
            <ToggleButton value="365days">1Y</ToggleButton>
          </ToggleButtonGroup>
          {rangeLabel && (
            <Box sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              height: 38,
              px: 1.25,
              borderRadius: 999,
              backgroundColor: ACCENT_SOFT,
              border: `1px solid ${ACCENT_LIGHT}`,
              boxSizing: 'border-box'
            }}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#365314', lineHeight: 1 }}>
                {rangeLabel}
              </Typography>
              <IconButton size="small" onClick={clearCustomRange} sx={{ p: 0.25 }}>
                <CloseIcon sx={{ fontSize: 13, color: '#365314' }} />
              </IconButton>
            </Box>
          )}
          <IconButton
            onClick={(e) => setCalAnchor(e.currentTarget)}
            sx={{
              backgroundColor: SURFACE,
              border: `1px solid ${BORDER}`,
              borderRadius: 2,
              '&:hover': { backgroundColor: ACCENT_SOFT }
            }}
          >
            <CalendarMonthIcon sx={{ fontSize: 20, color: TEXT }} />
          </IconButton>
          <Popover
            open={Boolean(calAnchor)}
            anchorEl={calAnchor}
            onClose={() => setCalAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ sx: { p: 2, borderRadius: 2, mt: 1, minWidth: 280 } }}
          >
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, mb: 1.5, color: TEXT }}>
              Custom Date Range
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Stack spacing={1.5}>
                <DesktopDatePicker
                  label="From"
                  inputFormat="dd MMM yyyy"
                  value={customStart}
                  onChange={(v) => setCustomStart(v)}
                  renderInput={(params) => <TextField size="small" fullWidth {...params} />}
                />
                <DesktopDatePicker
                  label="To"
                  inputFormat="dd MMM yyyy"
                  value={customEnd}
                  onChange={(v) => setCustomEnd(v)}
                  minDate={customStart || undefined}
                  renderInput={(params) => <TextField size="small" fullWidth {...params} />}
                />
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button size="small" onClick={() => setCalAnchor(null)} sx={{ textTransform: 'none' }}>
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={applyCustomRange}
                    disabled={!customStart || !customEnd}
                    sx={{ textTransform: 'none', backgroundColor: TEXT, '&:hover': { backgroundColor: '#1e293b' } }}
                  >
                    Apply
                  </Button>
                </Stack>
              </Stack>
            </LocalizationProvider>
          </Popover>
          <Button
            onClick={() => navigate('/coa-dashboard')}
            startIcon={<InsightsIcon sx={{ color: '#15803d !important' }} />}
            sx={{
              backgroundColor: '#ecfccb',
              color: '#365314',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.82rem',
              borderRadius: 999,
              border: '1px solid #84cc16',
              boxShadow: 'none',
              px: 1.75,
              py: 0.6,
              '& .MuiButton-startIcon': { mr: 0.75 },
              '&:hover': { backgroundColor: '#d9f99d', borderColor: '#65a30d', boxShadow: '0 2px 4px rgba(132,204,22,0.25)' }
            }}
          >
            CoA Dashboard
          </Button>
          <IconButton
            onClick={fetchDashboardData}
            disabled={isLoading}
            sx={{
              backgroundColor: SURFACE,
              border: `1px solid ${BORDER}`,
              borderRadius: 2,
              '&:hover': { backgroundColor: ACCENT_SOFT }
            }}
          >
            <RefreshIcon sx={{ fontSize: 20, color: TEXT }} />
          </IconButton>
        </Stack>
      </Stack>

      {/* Combined Metrics Row - 6 cards */}
      <Grid container spacing={{ xs: 1, sm: 1.5 }} sx={{ mb: 1.5 }}>
        <Grid item xs={6} sm={4} md={2}>
          <MetricCard
            title="Revenue (AR)"
            value={Utils.formatNumber(ARDashboard.numbers?.dashboardNumbers?.totalAR || 0, true, false)}
            trend={ARDashboard.sales?.dashboardSales?.trend}
            icon={<AccountBalanceWalletIcon />}
            accent={ACCENT}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <MetricCard
            title="Collection"
            value={`${ARDashboard.numbers?.dashboardNumbers?.totalARDSO || 0}d`}
            icon={<AccessTimeIcon />}
            accent="#0ea5e9"
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <MetricCard
            title="Sales Value"
            value={Utils.formatNumber(ARDashboard.sales?.dashboardSales?.salesValue || 0, true, false)}
            trend={ARDashboard.sales?.dashboardSales?.trend}
            icon={<TrendingUpIcon />}
            accent="#22c55e"
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <MetricCard
            title="Payables (AP)"
            value={Utils.formatNumber(APDashboard.numbers?.dashboardNumbers?.totalAP || 0, true, false)}
            trend={APDashboard.sales?.dashboardSales?.trend}
            icon={<ShoppingCartIcon />}
            accent="#f59e0b"
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <MetricCard
            title="Payment"
            value={`${APDashboard.numbers?.dashboardNumbers?.totalAPDSO || 0}d`}
            icon={<AccessTimeIcon />}
            accent="#78716c"
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <MetricCard
            title="Purchase Value"
            value={Utils.formatNumber(APDashboard.sales?.dashboardSales?.salesValue || 0, true, false)}
            trend={APDashboard.sales?.dashboardSales?.trend}
            icon={<PaymentIcon />}
            accent="#64748b"
          />
        </Grid>
      </Grid>

      {/* Row 1: Trend chart + Aging donut + Top Customers */}
      <Grid container spacing={{ xs: 1, sm: 1.5 }} sx={{ mb: 1.5 }}>
        <Grid item xs={12} md={5}>
          <Paper sx={{ ...cardSx, p: { xs: 1.5, sm: 2 }, height: '100%', overflow: 'hidden' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: '0.85rem' }}>
                Sales vs Purchase
              </Typography>
              <Chip
                size="small"
                icon={<NorthEastIcon sx={{ fontSize: 11, color: '#16a34a !important' }} />}
                label="Live"
                sx={{ backgroundColor: ACCENT_SOFT, color: '#365314', fontWeight: 600, fontSize: '0.62rem', height: 18, '& .MuiChip-label': { px: 0.75 } }}
              />
            </Stack>
            {isLoading && ARDashboard.sales?.status !== ApiStatus.SUCCESS ? (
              <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 2 }} />
            ) : (
              <Chart options={salesChartOptions as any} series={salesChartSeries as any} type="line" height={220} />
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ ...cardSx, p: { xs: 1.5, sm: 2 }, height: '100%', overflow: 'hidden' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 0.5 }}>
              <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: '0.85rem' }}>
                Aging Analysis
              </Typography>
              <Box textAlign="right">
                <Typography sx={{ fontSize: '0.6rem', color: MUTED, lineHeight: 1 }}>Total</Typography>
                <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: '0.78rem', lineHeight: 1.3 }}>
                  {Utils.formatNumber(ARDashboard.aging?.dashboardAging?.agingValue || 0, true, false)}
                </Typography>
              </Box>
            </Stack>
            {isLoading && ARDashboard.aging?.status !== ApiStatus.SUCCESS ? (
              <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 2 }} />
            ) : agingChartSeries.length === 0 ? (
              <Box sx={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: MUTED, fontSize: '0.8rem' }}>
                No aging data
              </Box>
            ) : (
              <Chart options={agingChartOptions} series={agingChartSeries} type="donut" height={220} />
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper sx={{ ...cardSx, p: { xs: 1.5, sm: 2 }, height: '100%', overflow: 'hidden' }}>
            <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: '0.85rem', mb: 0.5 }}>
              Top Customers · Outstanding
            </Typography>
            <Box>
              {isLoading && ARDashboard.customerDue?.status !== ApiStatus.SUCCESS ? (
                [...Array(4)].map((_, i) => <Skeleton key={i} height={42} sx={{ mb: 0.25 }} />)
              ) : (ARDashboard.customerDue?.dashboardCustomerDue?.list?.length || 0) === 0 ? (
                <Box sx={{ minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: MUTED, fontSize: '0.8rem' }}>No data</Box>
              ) : (
                ARDashboard.customerDue?.dashboardCustomerDue?.list?.slice(0, 4).map((item: any, index: number) => (
                  <PerformerRow key={index} item={item} index={index} color={avatarColors[index % avatarColors.length]} />
                ))
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Row 2: Sales/Purchase bars + Top Vendors */}
      <Grid container spacing={{ xs: 1, sm: 1.5 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Paper sx={{ ...cardSx, p: { xs: 1.5, sm: 2 }, height: '100%', overflow: 'hidden' }}>
            <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: '0.85rem', mb: 0.5 }}>
              Sales vs Received
            </Typography>
            {isLoading && (ARDashboard.sales?.status !== ApiStatus.SUCCESS || ARDashboard.customerDue?.status !== ApiStatus.SUCCESS) ? (
              <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
            ) : (
              <Chart options={salesReceivedChartOptions} series={salesReceivedChartSeries} type="bar" height={200} />
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper sx={{ ...cardSx, p: { xs: 1.5, sm: 2 }, height: '100%', overflow: 'hidden' }}>
            <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: '0.85rem', mb: 0.5 }}>
              Purchase vs Paid
            </Typography>
            {isLoading && (APDashboard.sales?.status !== ApiStatus.SUCCESS || APDashboard.customerDue?.status !== ApiStatus.SUCCESS) ? (
              <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
            ) : (
              <Chart options={purchasePaidChartOptions} series={purchasePaidChartSeries} type="bar" height={200} />
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ ...cardSx, p: { xs: 1.5, sm: 2 }, height: '100%', overflow: 'hidden' }}>
            <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: '0.85rem', mb: 0.5 }}>
              Top Vendors · Outstanding
            </Typography>
            <Box>
              {isLoading && APDashboard.customerDue?.status !== ApiStatus.SUCCESS ? (
                [...Array(4)].map((_, i) => <Skeleton key={i} height={42} sx={{ mb: 0.25 }} />)
              ) : (APDashboard.customerDue?.dashboardCustomerDue?.list?.length || 0) === 0 ? (
                <Box sx={{ minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: MUTED, fontSize: '0.8rem' }}>No data</Box>
              ) : (
                APDashboard.customerDue?.dashboardCustomerDue?.list?.slice(0, 4).map((item: any, index: number) => (
                  <PerformerRow key={index} item={item} index={index} color={avatarColors[(index + 2) % avatarColors.length]} />
                ))
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Home;
